import { FormGroup } from '@angular/forms';
import type { FormField, ReactiveFieldLogicContext } from '../../interfaces/form.interface';
import type { ReactiveChangeOrigin } from '../change-origin/reactive-change-origin';
import type { ReactiveDependencyGraph } from '../dependencies/reactive-dependency-graph';
import type { DropdownData } from '../../interfaces/input.interface';
import { DropdownFilterExecutor } from './dropdown-filter.executor';
import { DropdownTransformExecutor } from './dropdown-transform.executor';
import { FieldUpdateExecutor } from './field-update.executor';
import { FormFieldFinder } from './form-field.finder';
import { FormOperationState } from './form-operation-state';

/** Orchestrates field-level dynamic logic in a deterministic order. */
export class FieldLogicExecutor<T> {
    private pendingChangedField: string | null | undefined;
    private pendingChangeOrigin: ReactiveChangeOrigin = 'programmatic';

    private currentExecution: Promise<void> | null = null;

    constructor(
        private readonly fieldFinder: FormFieldFinder<T>,
        private readonly model: FormGroup,
        private readonly transformExecutor: DropdownTransformExecutor<T>,
        private readonly filterExecutor: DropdownFilterExecutor<T>,
        private readonly updateExecutor: FieldUpdateExecutor<T>,
        private readonly state: FormOperationState,
        private readonly dependencyGraph: ReactiveDependencyGraph<T>,
    ) {}

    public execute(changedField: string | null, origin: ReactiveChangeOrigin = 'programmatic'): Promise<void> {
        this.pendingChangedField = this.pendingChangedField === undefined
            ? changedField
            : null;
        this.pendingChangeOrigin = this.pendingChangedField === null ? 'programmatic' : origin;

        if (this.currentExecution) {
            return this.currentExecution;
        }

        this.currentExecution = this.drainQueue()
            .finally(() => {
                this.currentExecution = null;
            });

        return this.currentExecution;
    }

    private async drainQueue(): Promise<void> {
        try {
            while (this.pendingChangedField !== undefined) {
                const changedField = this.pendingChangedField;
                const origin = this.pendingChangeOrigin;

                this.pendingChangedField = undefined;
                this.pendingChangeOrigin = 'programmatic';
                await this.executeAsync(changedField, origin);
            }
        } finally {
            this.pendingChangedField = undefined;
        }
    }

    private async executeAsync(changedField: string | null, origin: ReactiveChangeOrigin): Promise<void> {
        const fields = this.fieldFinder.getFields().filter(field => this.shouldExecuteFieldLogic(field, changedField));

        for (const field of fields) {
            await this.executeFieldLogic(field, changedField, origin);
        }
    }

    private async executeFieldLogic(field: FormField<T>, changedField: string | null, origin: ReactiveChangeOrigin): Promise<void> {
        if (field.input?.dropdownConfiguration?.transform) {
            this.transformExecutor.applyTransforms(field);
        }

        if (field.input?.dropdownConfiguration?.filter) {
            const selectedValueChanged = this.filterExecutor.applyFilter(field, changedField, origin);

            if (selectedValueChanged && field.name) {
                void this.execute(field.name);
            }
        }

        const logicContext = this.createFieldLogicContext(field, changedField, origin);

        if (field.input?.dropdownConfiguration?.swap) {
            field.input.dropdownConfiguration.swap(field, logicContext);
        }

        if (this.shouldExecuteVisibility(field, changedField)) {
            field.turnVisible?.(field, logicContext);
        }

        if (typeof field.input?.dropdownConfiguration?.appendData === 'function') {
            await field.input.dropdownConfiguration.appendData(field, logicContext);
        }

        this.applyDynamicInputType(field, changedField);

        if (this.shouldExecuteComputedField(field, changedField)) {
            this.updateExecutor.applyComputedValue(field);
        }

        if (!field.computed) {
            this.updateExecutor.applyUpdateValue(field, logicContext);
        }
    }


    private createFieldLogicContext(
        field: FormField<T>,
        changedField: string | null,
        changeOrigin: ReactiveChangeOrigin,
    ): ReactiveFieldLogicContext<T> {
        return {
            field,
            model: this.model,
            changedField,
            changeOrigin,
            currentValue: this.model.get(field.name)?.value,
            getValue: (fieldName: string): unknown => this.model.get(fieldName)?.value,
            getRawValue: (): Record<string, unknown> => this.model.getRawValue() as Record<string, unknown>,
        };
    }

    private shouldExecuteFieldLogic(field: FormField<T>, changedField: string | null): boolean {
        return this.shouldExecuteFilter(field, changedField) ||
            this.hasTransform(field) ||
            this.hasUpdateValue(field) ||
            this.shouldExecuteComputedField(field, changedField) ||
            this.hasAppendData(field, changedField) ||
            this.shouldExecuteDynamicInputType(field, changedField) ||
            Boolean(field.input?.dropdownConfiguration?.swap) ||
            this.shouldExecuteVisibility(field, changedField);
    }

    private shouldExecuteFilter(field: FormField<T>, changedField: string | null): boolean {
        return Boolean(
            field.input?.dropdownConfiguration?.filter &&
            field.input.data &&
            this.shouldRunForDependency(field, changedField, 'filter'),
        );
    }

    private shouldExecuteComputedField(field: FormField<T>, changedField: string | null): boolean {
        if (!field.computed) {
            return false;
        }

        if (changedField === null) {
            return true;
        }

        if (field.computed.dependsOn?.length) {
            return field.computed.dependsOn.includes(changedField);
        }

        if (!this.dependencyGraph.hasDependencies(field.name, 'computed')) {
            return true;
        }

        return this.dependencyGraph.hasChangedDependency(field.name, [changedField], 'computed');
    }


    private shouldExecuteVisibility(field: FormField<T>, changedField: string | null): boolean {
        return Boolean(field.turnVisible && this.shouldRunForDependency(field, changedField, 'visibility'));
    }

    private shouldExecuteDynamicInputType(field: FormField<T>, changedField: string | null): boolean {
        const config = field.input?.dynamicTypeConfiguration;

        if (!config) {
            return false;
        }

        return this.shouldRunForDependency(field, changedField, 'dynamicType');
    }

    private shouldRunForDependency(
        field: FormField<T>,
        changedField: string | null,
        kind: 'filter' | 'appendData' | 'dynamicType' | 'visibility',
    ): boolean {
        if (changedField === null) {
            return true;
        }

        if (!this.dependencyGraph.hasDependencies(field.name, kind)) {
            return true;
        }

        return this.dependencyGraph.hasChangedDependency(field.name, [changedField], kind);
    }

    private applyDynamicInputType(field: FormField<T>, changedField: string | null): void {
        const config = field.input?.dynamicTypeConfiguration;

        if (!config || !this.shouldExecuteDynamicInputType(field, changedField)) {
            return;
        }

        const resolvedType = config.resolver({
            field,
            model: this.model,
            changedField,
            currentValue: this.model.get(field.name)?.value,
            defaultType: config.defaultType,
        });

        field.input.type = resolvedType;
    }

    private hasTransform(field: FormField<T>): boolean {
        return Boolean(field.input?.dropdownConfiguration?.transform && (field.input.data as DropdownData<T>));
    }

    private hasUpdateValue(field: FormField<T>): boolean {
        return Boolean(field.updateValue && !this.state.isProgrammaticChange);
    }

    private hasAppendData(field: FormField<T>, changedField: string | null): boolean {
        return Boolean(
            typeof field.input?.dropdownConfiguration?.appendData === 'function' &&
            this.shouldRunForDependency(field, changedField, 'appendData'),
        );
    }
}
